import React from 'react';
import { Edit2, FileText, Trash2 } from 'lucide-react';
import { AssetExpense } from '../AssetDetailScreen';

interface ExpenseListProps {
  expenses: AssetExpense[];
  handleDeleteExpense: (expenseId: string) => Promise<void>;
  handleEditExpense: (expense: AssetExpense) => void;
  currency: string;
  overseasCurrency: string;
}

export function ExpenseList({
  expenses,
  handleDeleteExpense,
  handleEditExpense,
  currency,
  overseasCurrency,
}: ExpenseListProps) {
  const formatMoney = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);

  return (
    <div className="space-y-2">
      {expenses.length === 0 ? (
        <p className="py-4 text-center text-sm text-zinc-500 italic">
          No expenses recorded yet.
        </p>
      ) : (
        expenses.map((expense) => (
          <div
            key={expense.id}
            className="rounded-2xl border border-zinc-100 bg-zinc-50 p-4 dark:border-zinc-900 dark:bg-zinc-950"
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h5 className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                    {expense.item}
                  </h5>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[8px] font-bold tracking-wider uppercase ${expense.category === 'Initial Cost' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'}`}
                  >
                    {expense.category}
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500">
                  {new Date(expense.date.replace(/-/g, '/')).toLocaleDateString(
                    'en-US',
                    {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }
                  )}{' '}
                  • C/O: {expense.personInCharge || 'N/A'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEditExpense(expense)}
                  className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-indigo-50 hover:text-indigo-500 dark:hover:bg-indigo-900/20"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                {expense.receipt && (
                  <a
                    href={expense.receipt}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                  >
                    <FileText className="h-4 w-4" />
                  </a>
                )}
                <button
                  onClick={() => handleDeleteExpense(expense.id)}
                  className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-zinc-100 pt-3 dark:border-zinc-900">
                <div className="flex flex-col">
                  <span className="text-[8px] font-black tracking-tighter text-zinc-400 uppercase">
                    Local Cost
                  </span>
                  <span className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
                    {formatMoney(expense.localCost, currency)}
                  </span>
                </div>
                {expense.foreignCost > 0 && (
                  <div className="flex flex-col">
                    <span className="text-[8px] font-black tracking-tighter text-zinc-400 uppercase">
                      Foreign Cost
                    </span>
                    <span className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
                      {formatMoney(expense.foreignCost, overseasCurrency)}
                    </span>
                  </div>
                )}
                {expense.foreignCost > 0 && expense.localCost > 0 && (
                  <div className="ml-auto flex flex-col items-end">
                    <span className="text-[8px] font-black tracking-tighter text-zinc-400 uppercase">
                      Exchange Rate
                    </span>
                    <div className="rounded-lg bg-indigo-50 px-2 py-1 text-[9px] font-bold text-indigo-600 uppercase dark:bg-indigo-900/30 dark:text-indigo-400">
                      1 {currency} ={' '}
                      {(expense.foreignCost / expense.localCost).toFixed(2)}{' '}
                      {overseasCurrency}
                    </div>
                  </div>
                )}
              </div>

              {expense.note && (
                <div className="rounded-xl bg-zinc-100/50 p-2.5 dark:bg-zinc-900/30">
                  <p className="text-[10px] leading-relaxed text-zinc-500 italic dark:text-zinc-400">
                    {expense.note}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
